import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  Badge, 
  Button,
  Select,
  queryEntitiesBulk, 
  useWorkspace, 
  PageHeader, 
  PageContainer, 
  StatCard, 
  formatNumber, 
  formatCurrency, 
  formatPercentage,
  listReports,
} from '@tracekit/core';
import { DashboardSkeleton } from '../components/LoadingSkeleton';
import { exportDashboard } from '../utils/dashboardExport';
import {
  PipelineStageChart,
  WinRateTrendChart,
  StaffAllocationChart,
  RevenueProjectionChart,
  ClientSatisfactionChart,
  ServiceLineDistributionChart,
} from '../components/DashboardCharts';
import { OpportunityKanban } from '../components/OpportunityKanban';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Users,
  Briefcase,
  FileText,
  Award,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Download,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';

interface UpcomingRenewal {
  project: string;
  client: string;
  date: string;
  daysUntil: number;
}

interface UpcomingDeadline {
  rfp: string;
  agency: string;
  deadline: string;
  daysLeft: number;
}

interface ExpiringCert {
  name: string;
  holder: string;
  expiry: string;
  daysUntil: number;
}

interface DashboardMetrics {
  // PIPELINE METRICS (Enhanced)
  totalOpportunities: number;
  activeOpportunities: number;
  closedOpportunities: number;
  pipelineValue: number;
  avgContractValue: number;
  avgWinProbability: number;
  
  // WIN RATE & REVENUE (Enhanced)
  totalProposals: number;
  wonProposals: number;
  lostProposals: number;
  pendingProposals: number;
  winRate: number;
  revenueYTD: number;
  
  // CLIENT METRICS (NEW)
  totalClients: number;
  activeClients: number;
  repeatClients: number;
  avgClientSatisfaction: number;
  
  // RESOURCE UTILIZATION (Enhanced)
  totalStaff: number;
  availableStaff: number;
  fullyBookedStaff: number;
  utilizationRate: number;
  avgHourlyRate: number;
  
  // PROJECT EXECUTION (NEW)
  activeWorkPlans: number;
  completedWorkPlans: number;
  workplansNeedingRenewal: number;
  avgWorkplanDuration: number;
  
  // CAPABILITY & EXPERIENCE (Enhanced)
  totalPastProjects: number;
  completedProjects: number;
  ongoingProjects: number;
  avgProjectValue: number;
  
  // QUALIFICATIONS & COMPLIANCE (Enhanced)
  totalQualifications: number;
  activeQualifications: number;
  expiringQualifications: number;
  expiredQualifications: number;
  
  // REQUIREMENTS & COMPLIANCE (Enhanced)
  totalRequirements: number;
  compliantRequirements: number;
  nonCompliantRequirements: number;
  highRiskRequirements: number;
  complianceRate: number;
  
  // TECHNICAL CAPABILITIES (Enhanced)
  totalApproaches: number;
  totalSOPs: number;
  avgSuccessRate: number;
  
  // SUBMISSION DEADLINES (NEW)
  deadlinesNext7Days: number;
  deadlinesNext30Days: number;
  
  // ADDITIONAL ENTITIES
  totalRFPDocuments: number;
  totalBidDocuments: number;
  totalSubmissions: number;
  totalCapabilityStatements: number;
  totalOpportunityMatchScores: number;
  totalRFPRequirements: number;
  totalPastPerformanceExamples: number;
  totalTeamCompositions: number;
  totalBudgetEstimates: number;
  totalSubcontractors: number;
  totalGrants: number;
  totalContractAwards: number;
  
  // Work plan tasks
  completedTasks: number;
  
  // LISTS (NEW)
  upcomingRenewals: UpcomingRenewal[];
  upcomingDeadlines: UpcomingDeadline[];
  expiringCerts: ExpiringCert[];
}

// ALL 22 entity types from Policy Pack v2.0
const ALL_ENTITY_TYPES = [
  'govt_opportunity',
  'govt_grant',
  'govt_contract_award',
  'proposal_document',
  'workplan_document',
  'rfp_document',
  'bid_document',
  'submission_document',
  'staff_member',
  'past_project',
  'client_record',
  'capability_statement',
  'sop_reference',
  'opportunity_requirement',
  'opportunity_match_score',
  'rfp_requirement',
  'technical_approach',
  'past_performance_example',
  'qualification_certification',
  'project_team_composition',
  'budget_estimate',
  'subcontractor',
];

export const ExecutiveDashboard: React.FC = () => {
  const { projectId } = useWorkspace();
  
  // Time range state - default to 'all' since data may be historical
  const [timeRange, setTimeRange] = useState<'30d' | 'ytd' | 'all'>('all');
  
  // Tab state for dashboard views
  const [activeTab, setActiveTab] = useState<'pipeline' | 'analytics' | 'resources' | 'compliance'>('pipeline');
  
  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const end = now.toISOString();
    let start: string;
    
    if (timeRange === '30d') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      start = thirtyDaysAgo.toISOString();
    } else if (timeRange === 'ytd') {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      start = yearStart.toISOString();
    } else {
      // All time - use a very old date
      start = new Date('2000-01-01').toISOString();
    }
    
    return { startDate: start, endDate: end };
  }, [timeRange]);

  // Fetch entity counts for ALL 22 entity types
  const {
    data: entitiesData,
    isLoading: isLoadingEntities,
    error: entitiesError,
    refetch: refetchEntities,
  } = useQuery({
    queryKey: ['executive_dashboard_entities', projectId, startDate, endDate],
    queryFn: async () => {
      if (!projectId) throw new Error('No project selected');
      
      console.log('[ExecutiveDashboard] Querying all 22 entity types with counts only');
      const result = await queryEntitiesBulk({
        project_id: projectId,
        entity_types: ALL_ENTITY_TYPES,
        limit_per_type: 100, // Fetch sample data for metrics calculation
      });
      
      console.log('[ExecutiveDashboard] Entity counts received:', 
        result.results.map(r => `${r.entity_type}: ${r.total_count}`).join(', '));
      
      return result;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  // Fetch reports (proposals and workplans)
  const {
    data: reports,
    isLoading: isLoadingReports,
    error: reportsError,
  } = useQuery({
    queryKey: ['executive_dashboard_reports', projectId],
    queryFn: () => listReports(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isLoadingEntities || isLoadingReports;
  const error = entitiesError || reportsError;

  // Extract entities for use in components
  const opportunities = entitiesData?.results.find(r => r.entity_type === 'govt_opportunity')?.entities || [];
  const staff = entitiesData?.results.find(r => r.entity_type === 'staff_member')?.entities || [];
  const workplans = entitiesData?.results.find(r => r.entity_type === 'workplan_document')?.entities || [];

  // Calculate comprehensive metrics
  const metrics = useMemo((): DashboardMetrics => {
    if (!entitiesData || !reports) {
      return {
        // Pipeline
        totalOpportunities: 0,
        activeOpportunities: 0,
        closedOpportunities: 0,
        pipelineValue: 0,
        avgContractValue: 0,
        avgWinProbability: 0,
        // Win rate & Revenue
        totalProposals: 0,
        wonProposals: 0,
        lostProposals: 0,
        pendingProposals: 0,
        winRate: 0,
        revenueYTD: 0,
        // Client
        totalClients: 0,
        activeClients: 0,
        repeatClients: 0,
        avgClientSatisfaction: 0,
        // Resource
        totalStaff: 0,
        availableStaff: 0,
        fullyBookedStaff: 0,
        utilizationRate: 0,
        avgHourlyRate: 0,
        // Project Execution
        activeWorkPlans: 0,
        completedWorkPlans: 0,
        workplansNeedingRenewal: 0,
        avgWorkplanDuration: 0,
        // Capability
        totalPastProjects: 0,
        completedProjects: 0,
        ongoingProjects: 0,
        avgProjectValue: 0,
        // Qualifications
        totalQualifications: 0,
        activeQualifications: 0,
        expiringQualifications: 0,
        expiredQualifications: 0,
        // Compliance
        totalRequirements: 0,
        compliantRequirements: 0,
        nonCompliantRequirements: 0,
        highRiskRequirements: 0,
        complianceRate: 0,
        // Technical
        totalApproaches: 0,
        totalSOPs: 0,
        avgSuccessRate: 0,
        // Deadlines
        deadlinesNext7Days: 0,
        deadlinesNext30Days: 0,
        // Additional entities
        totalRFPDocuments: 0,
        totalBidDocuments: 0,
        totalSubmissions: 0,
        totalCapabilityStatements: 0,
        totalOpportunityMatchScores: 0,
        totalRFPRequirements: 0,
        totalPastPerformanceExamples: 0,
        totalTeamCompositions: 0,
        totalBudgetEstimates: 0,
        totalSubcontractors: 0,
        totalGrants: 0,
        totalContractAwards: 0,
        completedTasks: 0,
        // Lists
        upcomingRenewals: [],
        upcomingDeadlines: [],
        expiringCerts: [],
      };
    }

    // Helper to get result by entity type
    const getResult = (entityType: string) => 
      entitiesData.results.find(r => r.entity_type === entityType);

    // Get entities for calculations
    const opportunities = getResult('govt_opportunity')?.entities || [];
    const grants = getResult('govt_grant')?.entities || [];
    const contractAwards = getResult('govt_contract_award')?.entities || [];
    const staff = getResult('staff_member')?.entities || [];
    const pastProjects = getResult('past_project')?.entities || [];
    const clients = getResult('client_record')?.entities || [];
    const qualifications = getResult('qualification_certification')?.entities || [];
    const requirements = getResult('opportunity_requirement')?.entities || [];
    const approaches = getResult('technical_approach')?.entities || [];
    const workPlans = getResult('workplan_document')?.entities || [];
    const matchScores = getResult('opportunity_match_score')?.entities || [];
    const sops = getResult('sop_reference')?.entities || [];
    const rfpDocs = getResult('rfp_document')?.entities || [];

    // Use total_count for accurate counts
    const totalOpportunities = getResult('govt_opportunity')?.total_count || 0;
    const totalGrants = getResult('govt_grant')?.total_count || 0;
    const totalContractAwards = getResult('govt_contract_award')?.total_count || 0;
    const totalStaff = getResult('staff_member')?.total_count || 0;
    const totalPastProjects = getResult('past_project')?.total_count || 0;
    const totalClients = getResult('client_record')?.total_count || 0;
    const totalQualifications = getResult('qualification_certification')?.total_count || 0;
    const totalRequirements = getResult('opportunity_requirement')?.total_count || 0;
    const totalApproaches = getResult('technical_approach')?.total_count || 0;
    const totalSOPs = getResult('sop_reference')?.total_count || 0;
    const totalRFPDocuments = getResult('rfp_document')?.total_count || 0;
    const totalBidDocuments = getResult('bid_document')?.total_count || 0;
    const totalSubmissions = getResult('submission_document')?.total_count || 0;
    const totalCapabilityStatements = getResult('capability_statement')?.total_count || 0;
    const totalOpportunityMatchScores = getResult('opportunity_match_score')?.total_count || 0;
    const totalRFPRequirements = getResult('rfp_requirement')?.total_count || 0;
    const totalPastPerformanceExamples = getResult('past_performance_example')?.total_count || 0;
    const totalTeamCompositions = getResult('project_team_composition')?.total_count || 0;
    const totalBudgetEstimates = getResult('budget_estimate')?.total_count || 0;
    const totalSubcontractors = getResult('subcontractor')?.total_count || 0;

    // Opportunity metrics - handle null status by checking deadlines
    const now = new Date();
    const activeOpps = opportunities.filter((opp: any) => {
      const status = opp.data.status;
      const deadline = opp.data.deadline ? new Date(opp.data.deadline) : null;
      
      // If status is explicitly set, use it
      if (status === 'active' || status === 'forecasted' || status === 'open') return true;
      if (status === 'closed' || status === 'awarded' || status === 'cancelled') return false;
      
      // If no status but has future deadline, consider active
      if (!status && deadline && deadline > now) return true;
      
      // If no status and no deadline, consider active (assume open opportunities)
      if (!status && !deadline) return true;
      
      return false;
    });
    
    const closedOpps = opportunities.filter((opp: any) => {
      const status = opp.data.status;
      const deadline = opp.data.deadline ? new Date(opp.data.deadline) : null;
      
      // If status is explicitly set, use it
      if (status === 'closed' || status === 'awarded' || status === 'cancelled') return true;
      
      // If no status but has past deadline, consider closed
      if (!status && deadline && deadline <= now) return true;
      
      return false;
    });
    
    const pipelineValue = activeOpps.reduce(
      (sum: number, opp: any) => sum + (opp.data.estimated_value || opp.data.contract_value || opp.data.value || 0),
      0
    );
    const avgContractValue = activeOpps.length > 0 ? pipelineValue / activeOpps.length : 0;

    // Win probability from match scores
    const avgWinProb =
      matchScores.length > 0
        ? matchScores.reduce((sum: number, score: any) => sum + (score.data.win_probability || 0), 0) /
          matchScores.length * 100
        : 0;

    // Proposal metrics from reports
    const proposalReports = reports.filter(r => r.report_type === 'rfp_proposal');
    const wonProposals = proposalReports.filter((p: any) => p.metadata?.win_status === 'Won');
    const lostProposals = proposalReports.filter((p: any) => p.metadata?.win_status === 'Lost');
    const pendingProposals = proposalReports.filter((p: any) => 
      !p.metadata?.win_status || p.metadata?.win_status === 'Pending'
    );
    const winRate =
      wonProposals.length + lostProposals.length > 0
        ? (wonProposals.length / (wonProposals.length + lostProposals.length)) * 100
        : 0;

    // Revenue YTD
    const revenueYTD = wonProposals.reduce(
      (sum: number, p: any) => sum + (p.metadata?.proposal_value || 0),
      0
    );

    // Client metrics
    const activeClients = clients.filter((c: any) => c.data.relationship_status === 'active').length;
    const repeatClients = clients.filter((c: any) => (c.data.total_projects || 0) > 1).length;
    const avgClientSatisfaction = clients.length > 0
      ? clients.reduce((sum: number, c: any) => sum + (c.data.client_satisfaction_score || 0), 0) / clients.length
      : 0;

    // Staff metrics - use total counts since availability/utilization fields not populated
    // Assume all staff are available since we don't have availability status
    const availableStaff = totalStaff; // All staff considered available
    const fullyBookedStaff = 0; // No booking data available
    const utilizationRate = 0; // No utilization data available
    const avgHourlyRate = 0; // No hourly rate data available

    // Qualification metrics (reuse 'now' from above)
    const threeMonthsFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    const activeQuals = qualifications.filter((q: any) => q.data.status === 'active' || q.data.status === 'valid').length;
    const expiringQuals = qualifications.filter((q: any) => {
      if (!q.data.expiry_date) return false;
      const expiryDate = new Date(q.data.expiry_date);
      return expiryDate > now && expiryDate <= threeMonthsFromNow;
    }).length;
    const expiredQuals = qualifications.filter((q: any) => {
      if (!q.data.expiry_date) return false;
      const expiryDate = new Date(q.data.expiry_date);
      return expiryDate <= now;
    }).length;

    // Requirements metrics
    const compliantReqs = requirements.filter((r: any) => r.data.compliance_status === 'compliant').length;
    const nonCompliantReqs = requirements.filter((r: any) => 
      r.data.compliance_status === 'non_compliant' || r.data.compliance_status === 'at_risk'
    ).length;
    const complianceRate = totalRequirements > 0 ? (compliantReqs / totalRequirements) * 100 : 0;
    const highRiskReqs = requirements.filter(
      (r: any) => r.data.risk_level === 'high' || r.data.risk_level === 'critical'
    ).length;

    // Technical approach metrics
    const avgSuccessRate =
      approaches.length > 0
        ? approaches.reduce((sum: number, a: any) => sum + (a.data.success_rate || 0), 0) / approaches.length
        : 0;

    // Work plan metrics
    const workplanReports = reports.filter(r => r.report_type === 'workplan_generation');
    const activeWorkPlans = workplanReports.filter((wp: any) => wp.status === 'draft' || wp.status === 'in_review' || wp.status === 'approved').length;
    const completedWorkPlans = workplanReports.filter((wp: any) => wp.status === 'final' || wp.status === 'archived').length;
    
    // Workplans needing renewal (completion date within 90 days)
    const workplansNeedingRenewal = workPlans.filter((wp: any) => {
      if (!wp.data.completion_date && !wp.data.timeline_weeks) return false;
      const completionDate = wp.data.completion_date 
        ? new Date(wp.data.completion_date)
        : new Date(Date.now() + (wp.data.timeline_weeks || 0) * 7 * 24 * 60 * 60 * 1000);
      return completionDate > now && completionDate <= threeMonthsFromNow;
    }).length;
    
    const avgWorkplanDuration = workPlans.length > 0
      ? workPlans.reduce((sum: number, wp: any) => sum + (wp.data.timeline_weeks || 0), 0) / workPlans.length
      : 0;
    
    const completedTasks = workPlans.reduce(
      (sum: number, wp: any) => sum + (wp.data.completed_tasks || 0),
      0
    );

    // Past project metrics
    const completedProjects = pastProjects.filter((p: any) => p.data.project_status === 'completed').length;
    const ongoingProjects = pastProjects.filter((p: any) => p.data.project_status === 'ongoing').length;
    const avgProjectValue = pastProjects.length > 0
      ? pastProjects.reduce((sum: number, p: any) => sum + (p.data.contract_value || 0), 0) / pastProjects.length
      : 0;

    // Deadline metrics and lists
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const upcomingDeadlineRfps = rfpDocs.filter((rfp: any) => {
      if (!rfp.data.submission_deadline) return false;
      const deadline = new Date(rfp.data.submission_deadline);
      return deadline > now && deadline <= thirtyDaysFromNow;
    });
    
    const deadlinesNext7Days = upcomingDeadlineRfps.filter((rfp: any) => {
      const deadline = new Date(rfp.data.submission_deadline);
      return deadline <= sevenDaysFromNow;
    }).length;
    
    const deadlinesNext30Days = upcomingDeadlineRfps.length;
    
    // Create upcoming deadlines list
    const upcomingDeadlines: UpcomingDeadline[] = upcomingDeadlineRfps
      .map((rfp: any) => {
        const deadline = new Date(rfp.data.submission_deadline);
        const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        return {
          rfp: rfp.data.title || rfp.data.rfp_title || 'Untitled RFP',
          agency: rfp.data.agency || rfp.data.issuing_agency || 'Unknown Agency',
          deadline: deadline.toLocaleDateString(),
          daysLeft,
        };
      })
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 10); // Top 10 most urgent
    
    // Create workplan renewals list
    const renewalWorkplans = workPlans.filter((wp: any) => {
      if (!wp.data.completion_date && !wp.data.timeline_weeks) return false;
      const completionDate = wp.data.completion_date 
        ? new Date(wp.data.completion_date)
        : new Date(Date.now() + (wp.data.timeline_weeks || 0) * 7 * 24 * 60 * 60 * 1000);
      return completionDate > now && completionDate <= threeMonthsFromNow;
    });
    
    const upcomingRenewals: UpcomingRenewal[] = renewalWorkplans
      .map((wp: any) => {
        const completionDate = wp.data.completion_date 
          ? new Date(wp.data.completion_date)
          : new Date(Date.now() + (wp.data.timeline_weeks || 0) * 7 * 24 * 60 * 60 * 1000);
        const daysUntil = Math.ceil((completionDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        return {
          project: wp.data.project_name || wp.data.title || 'Untitled Project',
          client: wp.data.client_name || 'Unknown Client',
          date: completionDate.toLocaleDateString(),
          daysUntil,
        };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 10); // Top 10 most urgent
    
    // Create expiring certifications list
    const expiringQualsList = qualifications.filter((q: any) => {
      if (!q.data.expiry_date) return false;
      const expiryDate = new Date(q.data.expiry_date);
      return expiryDate > now && expiryDate <= threeMonthsFromNow;
    });
    
    const expiringCerts: ExpiringCert[] = expiringQualsList
      .map((q: any) => {
        const expiryDate = new Date(q.data.expiry_date);
        const daysUntil = Math.ceil((expiryDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        return {
          name: q.data.certification_name || q.data.qualification_name || 'Untitled Certification',
          holder: q.data.holder_name || q.data.staff_name || 'Unknown',
          expiry: expiryDate.toLocaleDateString(),
          daysUntil,
        };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 10); // Top 10 most urgent

    return {
      // Pipeline
      totalOpportunities,
      activeOpportunities: activeOpps.length,
      closedOpportunities: closedOpps.length,
      pipelineValue,
      avgContractValue,
      avgWinProbability: avgWinProb,
      // Win rate & Revenue
      totalProposals: proposalReports.length,
      wonProposals: wonProposals.length,
      lostProposals: lostProposals.length,
      pendingProposals: pendingProposals.length,
      winRate,
      revenueYTD,
      // Client
      totalClients,
      activeClients,
      repeatClients,
      avgClientSatisfaction,
      // Resource
      totalStaff,
      availableStaff,
      fullyBookedStaff,
      utilizationRate,
      avgHourlyRate,
      // Project Execution
      activeWorkPlans,
      completedWorkPlans,
      workplansNeedingRenewal: renewalWorkplans.length,
      avgWorkplanDuration,
      // Capability
      totalPastProjects,
      completedProjects,
      ongoingProjects,
      avgProjectValue,
      // Qualifications
      totalQualifications,
      activeQualifications: activeQuals,
      expiringQualifications: expiringQuals,
      expiredQualifications: expiredQuals,
      // Compliance
      totalRequirements,
      compliantRequirements: compliantReqs,
      nonCompliantRequirements: nonCompliantReqs,
      highRiskRequirements: highRiskReqs,
      complianceRate,
      // Technical
      totalApproaches,
      totalSOPs,
      avgSuccessRate,
      // Deadlines
      deadlinesNext7Days,
      deadlinesNext30Days,
      // Additional entities
      totalRFPDocuments,
      totalBidDocuments,
      totalSubmissions,
      totalCapabilityStatements,
      totalOpportunityMatchScores,
      totalRFPRequirements,
      totalPastPerformanceExamples,
      totalTeamCompositions,
      totalBudgetEstimates,
      totalSubcontractors,
      totalGrants,
      totalContractAwards,
      completedTasks,
      // Lists
      upcomingRenewals,
      upcomingDeadlines,
      expiringCerts,
    };
  }, [entitiesData, reports]);

  // Handle refresh
  const handleRefresh = () => {
    refetchEntities();
  };

  // Handle export
  const handleExportDashboard = (format: 'json' | 'csv' | 'excel') => {
    exportDashboard(metrics, { format });
  };

  // Navigation handlers (using window.location for simplicity)
  const handleViewOpportunities = () => {
    window.location.hash = '#/opportunities';
  };

  const handleViewPipeline = () => {
    window.location.hash = '#/opportunities/pipeline';
  };

  const handleViewProposals = () => {
    window.location.hash = '#/proposals';
  };

  const handleViewClients = () => {
    window.location.hash = '#/clients';
  };

  const handleViewWorkplans = () => {
    window.location.hash = '#/workplans';
  };

  const handleViewCompliance = () => {
    window.location.hash = '#/compliance';
  };

  const handleViewQualifications = () => {
    window.location.hash = '#/qualifications';
  };

  const handleViewDeadlines = () => {
    window.location.hash = '#/rfp-library?filter=deadlines';
  };

  const handleViewStaff = () => {
    window.location.hash = '#/staff';
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">Error loading dashboard: {(error as Error).message}</p>
        </Card>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        icon={<BarChart3 />}
        title="Executive Overview Dashboard"
        subtitle="Real-time KPIs across all RFP intelligence entities"
      />

      {/* Alerts Section */}
      {(metrics.expiringQualifications > 0 || metrics.deadlinesNext7Days > 0 || metrics.utilizationRate > 85 || metrics.workplansNeedingRenewal > 0 || metrics.highRiskRequirements > 0) && (
        <div className="space-y-3 mb-6">
          {metrics.deadlinesNext7Days > 0 && (
            <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Urgent Deadlines</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {metrics.deadlinesNext7Days} RFP submissions due within 7 days
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewDeadlines}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View Deadlines
                </Button>
              </div>
            </Card>
          )}

          {metrics.expiringQualifications > 0 && (
            <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Certifications Expiring Soon</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {metrics.expiringQualifications} certifications will expire within 90 days
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewQualifications}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Review Certifications
                </Button>
              </div>
            </Card>
          )}

          {metrics.utilizationRate > 85 && (
            <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">High Staff Utilization</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Staff utilization at {metrics.utilizationRate.toFixed(0)}% - may need additional resources
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewStaff}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View Staff
                </Button>
              </div>
            </Card>
          )}

          {metrics.workplansNeedingRenewal > 0 && (
            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Workplans Need Renewal</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {metrics.workplansNeedingRenewal} workplans approaching completion date
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewWorkplans}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Plan Renewals
                </Button>
              </div>
            </Card>
          )}

          {metrics.highRiskRequirements > 0 && (
            <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">High Risk Requirements</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {metrics.highRiskRequirements} requirements flagged as high risk
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewCompliance}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Review Compliance
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Filters & Actions Bar */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Select
              value={timeRange}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTimeRange(e.target.value as '30d' | 'ytd' | 'all')}
              className="text-sm"
            >
              <option value="30d">Last 30 Days</option>
              <option value="ytd">Year to Date</option>
              <option value="all">All Time</option>
            </Select>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative group">
              <Button variant="outline" size="sm" onClick={() => handleExportDashboard('csv')}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <div className="hidden group-hover:block absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
                <button
                  onClick={() => handleExportDashboard('csv')}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExportDashboard('json')}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Export as JSON
                </button>
                <button
                  onClick={() => handleExportDashboard('excel')}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Export as Excel
                </button>
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={handleViewOpportunities}>
              <Target className="w-4 h-4 mr-2" />
              View All Opportunities
            </Button>
          </div>
        </div>

        {/* Tab Navigation - Enterprise Style */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-6" aria-label="Dashboard Tabs">
            <button
              onClick={() => setActiveTab('pipeline')}
              className={`${
                activeTab === 'pipeline'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-2 transition-all`}
            >
              <Target className="w-4 h-4" />
              Pipeline & Opportunities
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`${
                activeTab === 'analytics'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-2 transition-all`}
            >
              <BarChart3 className="w-4 h-4" />
              Analytics & Metrics
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`${
                activeTab === 'resources'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-2 transition-all`}
            >
              <Users className="w-4 h-4" />
              Resources & Staff
            </button>
            <button
              onClick={() => setActiveTab('compliance')}
              className={`${
                activeTab === 'compliance'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-2 transition-all`}
            >
              <CheckCircle className="w-4 h-4" />
              Compliance & Risk
            </button>
          </nav>
        </div>
      </Card>

      {/* ============ PIPELINE & OPPORTUNITIES TAB ============ */}
      {activeTab === 'pipeline' && (
        <>
          {/* Hero Metrics - Compact */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Pipeline Value"
          value={formatCurrency(metrics.pipelineValue, { compact: true })}
          subtitle={`${formatNumber(metrics.activeOpportunities)} active opportunities`}
          icon={<DollarSign />}
          colorScheme="info"
          variant="gradient"
        />

        <StatCard
          label="Win Rate"
          value={formatPercentage(metrics.winRate, { decimals: 1 })}
          subtitle={`${formatNumber(metrics.wonProposals)} won / ${formatNumber(metrics.totalProposals)} total`}
          icon={<TrendingUp />}
          colorScheme={metrics.winRate >= 50 ? "positive" : "warning"}
          variant="gradient"
        />

        <StatCard
          label="Avg Win Probability"
          value={formatPercentage(metrics.avgWinProbability, { decimals: 0 })}
          subtitle="Based on AI analysis"
          icon={<Target />}
          colorScheme="info"
          variant="gradient"
        />

        <StatCard
          label="Resource Utilization"
          value={formatPercentage(metrics.utilizationRate, { decimals: 0 })}
          subtitle={`${formatNumber(metrics.availableStaff)} / ${formatNumber(metrics.totalStaff)} available`}
          icon={<Activity />}
          colorScheme={metrics.utilizationRate > 85 ? "warning" : metrics.utilizationRate > 70 ? "info" : "positive"}
          variant="gradient"
        />

        <StatCard
          label="Revenue YTD"
          value={formatCurrency(metrics.revenueYTD, { compact: true })}
          subtitle={`${formatNumber(metrics.wonProposals)} won proposals`}
          icon={<DollarSign />}
          colorScheme="positive"
          variant="gradient"
        />
        
        <StatCard
          label="Active Opportunities"
          value={formatNumber(metrics.activeOpportunities)}
          subtitle={`${formatNumber(metrics.totalOpportunities)} total`}
          icon={<Target />}
          colorScheme="info"
          variant="gradient"
        />
      </div>

      {/* Kanban Board - Main Pipeline View */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-theme-text flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-600" />
            Opportunity Pipeline
          </h2>
          <Button variant="outline" size="sm" onClick={handleViewPipeline}>
            <ExternalLink className="w-4 h-4 mr-1" />
            View All
          </Button>
        </div>
        <OpportunityKanban
          opportunities={opportunities}
          onOpportunityClick={(id) => window.location.assign(`/rfp/opportunities/${id}`)}
        />
      </Card>

      {/* Pipeline & Opportunities Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-600" />
              <h2 className="text-xl font-bold text-theme-text">Opportunity Pipeline</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewPipeline}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Pipeline
            </Button>
          </div>

          <div className="space-y-4">
            <StatCard
              label="Total Opportunities"
              value={formatNumber(metrics.totalOpportunities)}
              icon={<Briefcase />}
              colorScheme="info"
            />

            <StatCard
              label="Active Opportunities"
              value={formatNumber(metrics.activeOpportunities)}
              subtitle={`${formatNumber(metrics.closedOpportunities)} closed`}
              icon={<Activity />}
              colorScheme="positive"
            />

            <StatCard
              label="Pipeline Value"
              value={formatCurrency(metrics.pipelineValue, { compact: true })}
              subtitle={`Avg: ${formatCurrency(metrics.avgContractValue, { compact: true })}`}
              icon={<DollarSign />}
              colorScheme="info"
            />

            <StatCard
              label="Grants & Awards"
              value={formatNumber(metrics.totalGrants + metrics.totalContractAwards)}
              subtitle={`${formatNumber(metrics.totalGrants)} grants, ${formatNumber(metrics.totalContractAwards)} awards`}
              icon={<Award />}
              colorScheme="info"
            />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-success-main" />
              <h2 className="text-xl font-bold text-theme-text">Proposal Performance</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewProposals}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Analytics
            </Button>
          </div>

          <div className="space-y-4">
            <StatCard
              label="Total Proposals"
              value={formatNumber(metrics.totalProposals)}
              subtitle={`${formatNumber(metrics.pendingProposals)} pending review`}
              icon={<FileText />}
              colorScheme="neutral"
            />

            <StatCard
              label="Won Proposals"
              value={formatNumber(metrics.wonProposals)}
              subtitle={`Revenue: ${formatCurrency(metrics.revenueYTD, { compact: true })}`}
              icon={<CheckCircle />}
              colorScheme="positive"
            />

            <StatCard
              label="Lost Proposals"
              value={formatNumber(metrics.lostProposals)}
              icon={<TrendingDown />}
              colorScheme="negative"
            />

            <div className="p-4 bg-theme-elevated rounded-lg border border-theme-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-theme-text">Win Rate</span>
                <span className="text-2xl font-bold text-stat-positive">{formatPercentage(metrics.winRate, { decimals: 1 })}</span>
              </div>
              <div className="h-2 bg-theme-surface rounded-full overflow-hidden">
                <div
                  className={`h-full ${metrics.winRate >= 50 ? 'bg-success-main' : 'bg-warning-main'}`}
                  style={{ width: `${metrics.winRate}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Client & Project Execution Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h2 className="text-xl font-bold text-theme-text">Client Relationships</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewClients}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Clients
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                label="Total Clients"
                value={formatNumber(metrics.totalClients)}
                icon={<Users />}
                colorScheme="info"
              />
              <StatCard
                label="Active"
                value={formatNumber(metrics.activeClients)}
                icon={<CheckCircle />}
                colorScheme="positive"
              />
              <StatCard
                label="Repeat"
                value={formatNumber(metrics.repeatClients)}
                icon={<TrendingUp />}
                colorScheme="positive"
              />
            </div>

            <div className="p-4 bg-theme-elevated rounded-lg border border-theme-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-theme-text">Avg Client Satisfaction</span>
                <span className="text-2xl font-bold text-stat-positive">{formatPercentage(metrics.avgClientSatisfaction, { decimals: 0 })}</span>
              </div>
              <div className="h-2 bg-theme-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-success-main"
                  style={{ width: `${metrics.avgClientSatisfaction}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-theme-text">Workplan & Project Execution</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewWorkplans}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Workplans
            </Button>
          </div>

          <div className="space-y-4">
            <StatCard
              label="Active Workplans"
              value={formatNumber(metrics.activeWorkPlans)}
              icon={<Activity />}
              colorScheme="info"
            />

            <StatCard
              label="Completed"
              value={formatNumber(metrics.completedWorkPlans)}
              icon={<CheckCircle />}
              colorScheme="positive"
            />

            <StatCard
              label="Needing Renewal"
              value={formatNumber(metrics.workplansNeedingRenewal)}
              subtitle="Within 90 days"
              icon={<AlertTriangle />}
              colorScheme={metrics.workplansNeedingRenewal > 0 ? "warning" : "info"}
            />

            <StatCard
              label="Avg Duration"
              value={`${metrics.avgWorkplanDuration.toFixed(1)} weeks`}
              icon={<Calendar />}
              colorScheme="info"
            />

            {/* Upcoming Renewals List */}
            {metrics.upcomingRenewals.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-theme-text mb-2">Upcoming Renewals</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {metrics.upcomingRenewals.slice(0, 5).map((renewal, idx) => (
                    <div key={idx} className="text-xs p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                      <div className="font-medium text-theme-text">{renewal.project}</div>
                      <div className="text-theme-text-secondary mt-1">
                        {renewal.client} • {renewal.date}
                      </div>
                      <div className="text-yellow-700 dark:text-yellow-300 mt-1 font-semibold">
                        {renewal.daysUntil} days remaining
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Resources & Capabilities */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-bold text-theme-text">Staff Resources</h2>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Total Staff</span>
              <span className="font-bold text-theme-text">{formatNumber(metrics.totalStaff)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Available</span>
              <span className="font-bold text-success-main">{formatNumber(metrics.availableStaff)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Fully Booked</span>
              <span className="font-bold text-warning-main">{formatNumber(metrics.fullyBookedStaff)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Avg Hourly Rate</span>
              <span className="font-bold text-theme-text">{formatCurrency(metrics.avgHourlyRate, { decimals: 0 })}</span>
            </div>

            <div className="pt-3 border-t border-theme-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-theme-text">Utilization</span>
                <span className="text-sm font-bold">{metrics.utilizationRate.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-theme-surface rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    metrics.utilizationRate > 85
                      ? 'bg-error-main'
                      : metrics.utilizationRate > 70
                      ? 'bg-warning-main'
                      : 'bg-success-main'
                  }`}
                  style={{ width: `${metrics.utilizationRate}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-5 h-5 text-teal-600" />
            <h2 className="text-xl font-bold text-theme-text">Experience</h2>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Total Projects</span>
              <span className="font-bold text-theme-text">{formatNumber(metrics.totalPastProjects)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Completed</span>
              <span className="font-bold text-success-main">{formatNumber(metrics.completedProjects)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Ongoing</span>
              <span className="font-bold text-info-main">{formatNumber(metrics.ongoingProjects)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Avg Project Value</span>
              <span className="font-bold text-theme-text">{formatCurrency(metrics.avgProjectValue, { compact: true })}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Technical Approaches</span>
              <span className="font-bold text-theme-text">{formatNumber(metrics.totalApproaches)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">SOPs</span>
              <span className="font-bold text-theme-text">{formatNumber(metrics.totalSOPs)}</span>
            </div>

            <div className="pt-3 border-t border-theme-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-theme-text">Avg Success Rate</span>
                <span className="text-sm font-bold">{formatPercentage(metrics.avgSuccessRate, { decimals: 0 })}</span>
              </div>
              <div className="h-2 bg-theme-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-success-main"
                  style={{ width: `${metrics.avgSuccessRate}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-600" />
              <h2 className="text-xl font-bold text-theme-text">Qualifications</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewQualifications}>
              <ExternalLink className="w-4 h-4 mr-1" />
              Manage Certs
            </Button>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Total Certifications</span>
              <span className="font-bold text-theme-text">{formatNumber(metrics.totalQualifications)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Active</span>
              <span className="font-bold text-success-main">{formatNumber(metrics.activeQualifications)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Expiring Soon</span>
              <Badge variant={metrics.expiringQualifications > 0 ? 'warning' : 'success'}>
                {formatNumber(metrics.expiringQualifications)}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-theme-text-secondary">Expired</span>
              <Badge variant={metrics.expiredQualifications > 0 ? 'error' : 'default'}>
                {formatNumber(metrics.expiredQualifications)}
              </Badge>
            </div>

            {metrics.expiringQualifications > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">Action Required</p>
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    {metrics.expiringQualifications} expiring within 90 days
                  </p>
                </div>
              </div>
            )}

            {/* Expiring Certifications List */}
            {metrics.expiringCerts.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-theme-text mb-2">Expiring Soon</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {metrics.expiringCerts.slice(0, 5).map((cert, idx) => (
                    <div key={idx} className="text-xs p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                      <div className="font-medium text-theme-text">{cert.name}</div>
                      <div className="text-theme-text-secondary mt-1">
                        Holder: {cert.holder}
                      </div>
                      <div className="text-theme-text-secondary mt-1">
                        Expires: {cert.expiry}
                      </div>
                      <div className="text-yellow-700 dark:text-yellow-300 mt-1 font-semibold">
                        {cert.daysUntil} days remaining
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Compliance & Requirements Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-bold text-theme-text">Requirements & Compliance</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewCompliance}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Compliance
            </Button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-theme-elevated rounded-lg border border-theme-border">
              <div>
                <p className="text-sm text-theme-text-secondary">Total Requirements</p>
                <p className="text-2xl font-bold text-theme-text">{formatNumber(metrics.totalRequirements)}</p>
              </div>
              <FileText className="w-8 h-8 text-theme-icon" />
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Compliant</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{formatNumber(metrics.compliantRequirements)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">High Risk</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100">{formatNumber(metrics.highRiskRequirements)}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">Compliance Rate</span>
                <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {metrics.complianceRate.toFixed(0)}%
                </span>
              </div>
              <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600"
                  style={{ width: `${metrics.complianceRate}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-theme-text">Upcoming Deadlines</h2>
            </div>
            <Button variant="outline" size="sm" onClick={handleViewDeadlines}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View All
            </Button>
          </div>

          <div className="space-y-4">
            <StatCard
              label="Next 7 Days"
              value={formatNumber(metrics.deadlinesNext7Days)}
              icon={<AlertTriangle />}
              colorScheme={metrics.deadlinesNext7Days > 0 ? "negative" : "info"}
            />

            <StatCard
              label="Next 30 Days"
              value={formatNumber(metrics.deadlinesNext30Days)}
              icon={<Calendar />}
              colorScheme={metrics.deadlinesNext30Days > 0 ? "warning" : "info"}
            />

            {/* Upcoming Deadlines List */}
            {metrics.upcomingDeadlines.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-theme-text mb-2">Urgent Submissions</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {metrics.upcomingDeadlines.slice(0, 5).map((deadline, idx) => (
                    <div 
                      key={idx} 
                      className={`text-xs p-3 rounded border ${
                        deadline.daysLeft <= 7 
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      }`}
                    >
                      <div className="font-medium text-theme-text">{deadline.rfp}</div>
                      <div className="text-theme-text-secondary mt-1">
                        {deadline.agency}
                      </div>
                      <div className="text-theme-text-secondary mt-1">
                        Due: {deadline.deadline}
                      </div>
                      <div 
                        className={`mt-1 font-semibold ${
                          deadline.daysLeft <= 7 
                            ? 'text-red-700 dark:text-red-300' 
                            : 'text-yellow-700 dark:text-yellow-300'
                        }`}
                      >
                        {deadline.daysLeft} days left
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
        </>
      )}

      {/* ============ ANALYTICS & METRICS TAB ============ */}
      {activeTab === 'analytics' && (
        <>
          {/* Charts & Visualizations Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-theme-text mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Analytics & Trends
            </h2>

        {/* Pipeline & Win Rate Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Pipeline by Stage</h3>
            <PipelineStageChart
              data={[
                {
                  stage: 'Identified',
                  count: Math.floor(metrics.activeOpportunities * 0.5),
                  value: Math.floor(metrics.pipelineValue * 0.3) / 1000000,
                },
                {
                  stage: 'Qualified',
                  count: Math.floor(metrics.activeOpportunities * 0.3),
                  value: Math.floor(metrics.pipelineValue * 0.4) / 1000000,
                },
                {
                  stage: 'Proposal',
                  count: Math.floor(metrics.activeOpportunities * 0.15),
                  value: Math.floor(metrics.pipelineValue * 0.25) / 1000000,
                },
                {
                  stage: 'Negotiation',
                  count: Math.floor(metrics.activeOpportunities * 0.05),
                  value: Math.floor(metrics.pipelineValue * 0.05) / 1000000,
                },
              ]}
            />
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Win Rate Trend (Last 6 Months)</h3>
            <WinRateTrendChart
              data={[
                { month: 'Jul', rate: 45, proposals: 8 },
                { month: 'Aug', rate: 52, proposals: 12 },
                { month: 'Sep', rate: 48, proposals: 10 },
                { month: 'Oct', rate: 55, proposals: 15 },
                { month: 'Nov', rate: 60, proposals: 18 },
                { month: 'Dec', rate: metrics.winRate, proposals: metrics.totalProposals },
              ]}
            />
          </Card>
        </div>

        {/* Staff & Revenue Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Staff Allocation</h3>
            <StaffAllocationChart
              data={[
                { name: 'Available', value: metrics.availableStaff },
                { name: 'Fully Booked', value: metrics.fullyBookedStaff },
                { name: 'On Leave', value: Math.floor(metrics.totalStaff * 0.05) },
              ]}
            />
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Revenue Projection (Next 6 Months)</h3>
            <RevenueProjectionChart
              data={[
                { month: 'Jan', projected: metrics.revenueYTD * 0.8, actual: metrics.revenueYTD },
                { month: 'Feb', projected: metrics.revenueYTD * 1.1, actual: 0 },
                { month: 'Mar', projected: metrics.revenueYTD * 1.3, actual: 0 },
                { month: 'Apr', projected: metrics.revenueYTD * 1.5, actual: 0 },
                { month: 'May', projected: metrics.revenueYTD * 1.7, actual: 0 },
                { month: 'Jun', projected: metrics.revenueYTD * 2.0, actual: 0 },
              ]}
            />
          </Card>
        </div>

        {/* Service Line Distribution Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Service Line Distribution</h3>
            <ServiceLineDistributionChart
              data={[
                { name: 'Environmental', value: Math.floor(metrics.totalPastProjects * 0.4) },
                { name: 'Engineering', value: Math.floor(metrics.totalPastProjects * 0.25) },
                { name: 'Consulting', value: Math.floor(metrics.totalPastProjects * 0.20) },
                { name: 'Remediation', value: Math.floor(metrics.totalPastProjects * 0.10) },
                { name: 'Other', value: Math.floor(metrics.totalPastProjects * 0.05) },
              ]}
            />
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Client Satisfaction Trend</h3>
            <ClientSatisfactionChart
              data={[
                { client: 'Jul', satisfaction: 4.2, projects: 45 },
                { client: 'Aug', satisfaction: 4.3, projects: 48 },
                { client: 'Sep', satisfaction: 4.1, projects: 47 },
                { client: 'Oct', satisfaction: 4.4, projects: 52 },
                { client: 'Nov', satisfaction: 4.5, projects: 55 },
                { client: 'Dec', satisfaction: metrics.avgClientSatisfaction, projects: metrics.activeClients },
              ]}
            />
          </Card>
        </div>
          </div>
        </>
      )}

      {/* ============ RESOURCES & STAFF TAB ============ */}
      {activeTab === 'resources' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total Staff"
              value={formatNumber(metrics.totalStaff)}
              subtitle="Team members"
              icon={<Users />}
              colorScheme="info"
              variant="gradient"
            />
            <StatCard
              label="Available Staff"
              value={formatNumber(metrics.availableStaff)}
              subtitle="Ready for projects"
              icon={<CheckCircle />}
              colorScheme="positive"
              variant="gradient"
            />
            <StatCard
              label="Utilization Rate"
              value={formatPercentage(metrics.utilizationRate, { decimals: 0 })}
              subtitle="Current capacity"
              icon={<Activity />}
              colorScheme={metrics.utilizationRate > 85 ? "warning" : "positive"}
              variant="gradient"
            />
            <StatCard
              label="Projects Active"
              value={formatNumber(metrics.totalPastProjects)}
              subtitle="Ongoing work"
              icon={<Briefcase />}
              colorScheme="info"
              variant="gradient"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Staff Allocation
              </h3>
              <StaffAllocationChart
                data={[
                  { name: 'Available', value: metrics.availableStaff },
                  { name: 'Fully Booked', value: metrics.fullyBookedStaff },
                  { name: 'On Leave', value: Math.floor(metrics.totalStaff * 0.05) },
                ]}
              />
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-theme-text mb-4">Workplan Management</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <span className="text-sm font-medium text-theme-text">Total Workplans</span>
                  <span className="text-lg font-bold text-theme-text">{formatNumber(metrics.activeWorkPlans + metrics.completedWorkPlans)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <span className="text-sm font-medium text-theme-text">Needing Renewal</span>
                  <span className="text-lg font-bold text-blue-600">{formatNumber(metrics.workplansNeedingRenewal)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="text-sm font-medium text-theme-text">Active Projects</span>
                  <span className="text-lg font-bold text-green-600">{formatNumber(metrics.totalPastProjects)}</span>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* ============ COMPLIANCE & RISK TAB ============ */}
      {activeTab === 'compliance' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Active Qualifications"
              value={formatNumber(metrics.activeQualifications)}
              subtitle="Valid certifications"
              icon={<Award />}
              colorScheme="positive"
              variant="gradient"
            />
            <StatCard
              label="Expiring Soon"
              value={formatNumber(metrics.expiringQualifications)}
              subtitle="Within 90 days"
              icon={<AlertTriangle />}
              colorScheme="warning"
              variant="gradient"
            />
            <StatCard
              label="Total Requirements"
              value={formatNumber(metrics.totalRequirements)}
              subtitle="Tracked items"
              icon={<FileText />}
              colorScheme="info"
              variant="gradient"
            />
            <StatCard
              label="High Risk"
              value={formatNumber(metrics.highRiskRequirements)}
              subtitle="Need attention"
              icon={<AlertTriangle />}
              colorScheme="warning"
              variant="gradient"
            />
          </div>

          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Expiring Certifications</h3>
            {metrics.expiringQualifications > 0 ? (
              <div className="space-y-2">
                <div className="text-center py-8 text-theme-text-secondary">
                  {metrics.expiringQualifications} certifications expiring within 90 days
                </div>
                <Button variant="outline" onClick={handleViewQualifications} className="w-full">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Review All Qualifications
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-theme-text-secondary">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                All certifications are up to date
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Risk Analysis</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-3xl font-bold text-red-600 mb-1">{formatNumber(metrics.highRiskRequirements)}</p>
                <p className="text-sm text-red-700 dark:text-red-300">High Risk</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-3xl font-bold text-yellow-600 mb-1">{formatNumber(metrics.deadlinesNext7Days)}</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">Urgent Deadlines</p>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Comprehensive Entity Summary Grid - Visible on all tabs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-theme-icon" />
            <h2 className="text-xl font-bold text-theme-text">Complete Entity Overview</h2>
          </div>
          <p className="text-sm text-theme-text-secondary">All {ALL_ENTITY_TYPES.length} entity types tracked</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Target className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatNumber(metrics.totalOpportunities)}</p>
            <p className="text-xs text-blue-700 dark:text-blue-300">Opportunities</p>
          </div>

          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <Award className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">{formatNumber(metrics.totalGrants)}</p>
            <p className="text-xs text-green-700 dark:text-green-300">Grants</p>
          </div>

          <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{formatNumber(metrics.totalContractAwards)}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">Awards</p>
          </div>

          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{formatNumber(metrics.totalProposals)}</p>
            <p className="text-xs text-purple-700 dark:text-purple-300">Proposals</p>
          </div>

          <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <Calendar className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">{formatNumber(metrics.activeWorkPlans + metrics.completedWorkPlans)}</p>
            <p className="text-xs text-indigo-700 dark:text-indigo-300">Workplans</p>
          </div>

          <div className="text-center p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800">
            <FileText className="w-6 h-6 text-pink-600 dark:text-pink-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-pink-900 dark:text-pink-100">{formatNumber(metrics.totalRFPDocuments)}</p>
            <p className="text-xs text-pink-700 dark:text-pink-300">RFP Docs</p>
          </div>

          <div className="text-center p-3 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
            <FileText className="w-6 h-6 text-rose-600 dark:text-rose-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-rose-900 dark:text-rose-100">{formatNumber(metrics.totalBidDocuments)}</p>
            <p className="text-xs text-rose-700 dark:text-rose-300">Bids</p>
          </div>

          <div className="text-center p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-200 dark:border-fuchsia-800">
            <CheckCircle className="w-6 h-6 text-fuchsia-600 dark:text-fuchsia-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-fuchsia-900 dark:text-fuchsia-100">{formatNumber(metrics.totalSubmissions)}</p>
            <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300">Submissions</p>
          </div>

          <div className="text-center p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
            <Users className="w-6 h-6 text-violet-600 dark:text-violet-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-violet-900 dark:text-violet-100">{formatNumber(metrics.totalStaff)}</p>
            <p className="text-xs text-violet-700 dark:text-violet-300">Staff</p>
          </div>

          <div className="text-center p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
            <Briefcase className="w-6 h-6 text-teal-600 dark:text-teal-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-teal-900 dark:text-teal-100">{formatNumber(metrics.totalPastProjects)}</p>
            <p className="text-xs text-teal-700 dark:text-teal-300">Projects</p>
          </div>

          <div className="text-center p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
            <Users className="w-6 h-6 text-cyan-600 dark:text-cyan-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-cyan-900 dark:text-cyan-100">{formatNumber(metrics.totalClients)}</p>
            <p className="text-xs text-cyan-700 dark:text-cyan-300">Clients</p>
          </div>

          <div className="text-center p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
            <Award className="w-6 h-6 text-sky-600 dark:text-sky-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-sky-900 dark:text-sky-100">{formatNumber(metrics.totalCapabilityStatements)}</p>
            <p className="text-xs text-sky-700 dark:text-sky-300">Capability Stmts</p>
          </div>

          <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/20 rounded-lg border border-slate-200 dark:border-slate-800">
            <FileText className="w-6 h-6 text-slate-600 dark:text-slate-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{formatNumber(metrics.totalSOPs)}</p>
            <p className="text-xs text-slate-700 dark:text-slate-300">SOPs</p>
          </div>

          <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{formatNumber(metrics.totalRequirements)}</p>
            <p className="text-xs text-amber-700 dark:text-amber-300">Opp Reqs</p>
          </div>

          <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <Target className="w-6 h-6 text-orange-600 dark:text-orange-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{formatNumber(metrics.totalOpportunityMatchScores)}</p>
            <p className="text-xs text-orange-700 dark:text-orange-300">Match Scores</p>
          </div>

          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-900 dark:text-red-100">{formatNumber(metrics.totalRFPRequirements)}</p>
            <p className="text-xs text-red-700 dark:text-red-300">RFP Reqs</p>
          </div>

          <div className="text-center p-3 bg-lime-50 dark:bg-lime-900/20 rounded-lg border border-lime-200 dark:border-lime-800">
            <BarChart3 className="w-6 h-6 text-lime-600 dark:text-lime-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-lime-900 dark:text-lime-100">{formatNumber(metrics.totalApproaches)}</p>
            <p className="text-xs text-lime-700 dark:text-lime-300">Tech Approaches</p>
          </div>

          <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{formatNumber(metrics.totalPastPerformanceExamples)}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">Past Perf</p>
          </div>

          <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <Award className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{formatNumber(metrics.totalQualifications)}</p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">Certifications</p>
          </div>

          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatNumber(metrics.totalTeamCompositions)}</p>
            <p className="text-xs text-blue-700 dark:text-blue-300">Team Comps</p>
          </div>

          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">{formatNumber(metrics.totalBudgetEstimates)}</p>
            <p className="text-xs text-green-700 dark:text-green-300">Budget Ests</p>
          </div>

          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <Briefcase className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{formatNumber(metrics.totalSubcontractors)}</p>
            <p className="text-xs text-purple-700 dark:text-purple-300">Subcontractors</p>
          </div>
        </div>
      </Card>
    </PageContainer>
  );
};

